/**
 * QRCodeDisplay Component
 *
 * Generates and displays a QR code for a given URL.
 * Includes download functionality for printing.
 *
 * Issue #832 - QR code club invites
 */

import { useState, useEffect, useCallback } from 'react';
import { Download, QrCode } from 'lucide-react';
import QRCode from 'qrcode';
import Button from './Button';

interface QRCodeDisplayProps {
  url: string;
  size?: number;
  title?: string;
  downloadFileName?: string;
  className?: string;
}

export default function QRCodeDisplay({
  url,
  size = 200,
  title,
  downloadFileName = 'qr-code',
  className = '',
}: QRCodeDisplayProps) {
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  // Generate QR code when URL changes
  useEffect(() => {
    if (!url) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Reset state when URL becomes empty
      setDataUrl(null);
      return;
    }

    setIsGenerating(true);
    setError(null);

    QRCode.toDataURL(url, {
      width: size * 2, // 2x for retina displays
      margin: 2,
      color: {
        dark: '#F9FAF9', // text-primary (light on dark theme)
        light: '#0C1310', // oro-bg (dark background)
      },
      errorCorrectionLevel: 'M',
    })
      .then((dataUrl) => {
        setDataUrl(dataUrl);
        setIsGenerating(false);
      })
      .catch((err) => {
        console.error('QR code generation failed:', err);
        setError('Failed to generate QR code');
        setIsGenerating(false);
      });
  }, [url, size]);

  // Download QR code as PNG
  const handleDownload = useCallback(() => {
    if (!dataUrl) return;

    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = `${downloadFileName}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [dataUrl, downloadFileName]);

  if (error) {
    return (
      <div className={`flex flex-col items-center justify-center p-4 ${className}`}>
        <div className="w-12 h-12 rounded-full bg-oro-error/10 flex items-center justify-center mb-2">
          <QrCode className="w-6 h-6 text-oro-error" />
        </div>
        <p className="text-sm text-oro-error">{error}</p>
      </div>
    );
  }

  if (isGenerating || !dataUrl) {
    return (
      <div className={`flex flex-col items-center justify-center p-4 ${className}`}>
        <div
          className="bg-oro-surface-alt border border-oro-border rounded-lg animate-pulse"
          style={{ width: size, height: size }}
        />
        <p className="text-sm text-text-muted mt-2">Generating QR code...</p>
      </div>
    );
  }

  return (
    <div className={`flex flex-col items-center ${className}`}>
      {title && (
        <p className="text-sm font-medium text-text-primary mb-3">{title}</p>
      )}

      {/* QR Code Image */}
      <div className="bg-oro-surface-alt border border-oro-border rounded-xl p-3 shadow-sm">
        <img
          src={dataUrl}
          alt="QR Code"
          width={size}
          height={size}
          className="rounded-lg"
        />
      </div>

      {/* Download Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={handleDownload}
        className="mt-3"
      >
        <Download className="w-4 h-4 mr-2" />
        Download PNG
      </Button>
    </div>
  );
}
